import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-background-200">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">该攻略文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors no-underline whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && (a.category === article.category || a.tags.some((t: string) => article.tags.includes(t))))
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: '撸啊撸攻略大全',
    },
    publisher: {
      '@type': 'Organization',
      name: '撸啊撸攻略大全',
    },
    inLanguage: 'zh-CN',
    articleSection: categoryInfo?.name || '',
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://lolguide.readdy.ai/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryInfo?.name || '攻略',
        item: `https://lolguide.readdy.ai/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 bg-background-50">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryInfo?.name || '攻略', href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
          />

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
          />

          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium no-underline hover:bg-primary-200 transition-colors"
              >
                {categoryInfo?.name}
              </Link>
              {article.tags.slice(0, 3).map((tag: string) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                阅读约 {article.readTime} 分钟
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-64 md:h-80 object-cover object-top"
              title={article.title}
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-10">
            <aside className="lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-24 bg-background-100 rounded-xl p-5 border border-background-200/70">
                <h4 className="font-heading text-sm text-foreground-950 mb-3">
                  目录
                </h4>
                <nav>
                  <ul className="space-y-2">
                    {article.content.sections.map((section, idx) => (
                      <li key={idx}>
                        <a
                          href={`#section-${idx}`}
                          className="text-sm text-foreground-600 hover:text-primary-500 transition-colors no-underline flex items-center gap-2"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                          {section.heading}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="prose-custom">
                <p className="text-base text-foreground-700 leading-relaxed mb-8">
                  {article.content.intro}
                </p>

                {article.content.sections.map((section, idx) => (
                  <section key={idx} id={`section-${idx}`} className="mb-8">
                    <h2 className="font-heading text-xl text-foreground-950 mb-3">
                      {section.heading}
                    </h2>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      {section.content}
                    </p>
                  </section>
                ))}
              </div>

              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-foreground-500">分享文章：</span>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link text-lg"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line text-lg"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line text-lg"></i>
                  </button>
                </div>
              </div>

              {relatedArticles.length > 0 && (
                <div className="mt-12 pt-8 border-t border-background-200/70">
                  <h3 className="font-heading text-lg text-foreground-950 mb-6">
                    相关推荐
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {relatedArticles.map((related) => (
                      <Link
                        key={related.id}
                        to={`/article/${related.slug}`}
                        className="group block p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all duration-300 no-underline cursor-pointer"
                      >
                        <h4 className="font-heading text-sm text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {related.title}
                        </h4>
                        <p className="text-xs text-foreground-500 line-clamp-2">
                          {related.excerpt}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}